import {
    ASDRatchets,
    AdditionalSettings,
    ContractTerms,
    InputDefinitions,
    PerformanceDiscounts,
    PlayCountAggregationTypeMappingList,
    RateMultiplierTypesEnum,
    RateMultipliers,
    Revenue,
    RoyaltyModelTypesEnum,
    RoyaltyModelsEnum,
    StreamingTaxDeductionCaps,
    SubscriberVolumeDiscount,
    TerritoryOverride,
    ThirdPartyDeductionCaps,
    VendorShareCalculationTypeMappingList
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import {
    ContractTermMultiplierKey,
    CorePlan,
    PaperPlanCorePlan,
    RateCardCoreRateGridColumnName,
    RateCardStatus,
    RateMultiplierDisplayName,
    UmgPerfDiscountType
} from 'src/constants/enums';
import {
    IAddRateCardModalValues,
    IFirstPartyRateGridRowData,
    IFullRateGridRowData,
    IRateCardCoreRateGridRowData,
    IRateCardValues,
    IRateCardsRowData,
    IThirdPartyData,
    IThirdPartyRateGridRowData
} from 'src/types/IRateCard';

// POPS 1.0 constants
export const PRECISION = { DEFAULT: 4, PERCENTAGE: 2, CPP: 6 };

// POPS 2.0 constants
export const defaultAddRateCardModalValues: IAddRateCardModalValues = {
    vendor: '',
    serviceId: '',
    month: '',
    description: '',
    templateRateCardId: ''
};

export const defaultRateCardValues: IRateCardValues = {
    contractName: '',
    description: '',
    isDeleted: false,
    isDraft: false,
    serviceId: 0,
    createdBy: '',
    creationDate: '',
    changedBy: '',
    changedDate: '',
    entityActiveIntervals: [],
    coreRates: [] as IRateCardCoreRateGridRowData[],
    contractTerms: {
        revenue: {} as Revenue,
        rateMultipliers: {} as RateMultipliers,
        asdRatchets: { inputDefinitions: {} as InputDefinitions } as ASDRatchets,
        thirdPartyDeductionCaps: { territoryOverrides: [] as TerritoryOverride[] } as ThirdPartyDeductionCaps,
        subscriberVolumeDiscount: {} as SubscriberVolumeDiscount,
        performanceDiscounts: [] as PerformanceDiscounts,
        additionalSettings: {} as AdditionalSettings,
        vendorShareCalculationMapping: [] as VendorShareCalculationTypeMappingList,
        playCountAggregationTypeMapping: [] as PlayCountAggregationTypeMappingList,
        streamingTaxDeductionCaps: [] as StreamingTaxDeductionCaps
    } as ContractTerms,
    firstPartyRates: [] as IFirstPartyRateGridRowData[],
    thirdPartyData: {
        thirdPartyRates: [] as IThirdPartyRateGridRowData[]
    } as IThirdPartyData,
    fullRates: [] as IFullRateGridRowData[],
    planConfiguration: {}
};

export const defaultIsRateCardInputValid: Record<string, boolean> = {
    coreRates: false,
    contractTerms: false,
    firstPartyRates: false,
    thirdPartyRates: false
};

export const defaultIsContractTermInputValid: Record<string, boolean> = {
    revenue: false,
    vendorSharePlayCountCalcStrategy: true,
    rateMultipliers: false,
    asdRatchets: true,
    thirdPartyDeductionCaps: false,
    subscriberVolumeDiscount: true,
    additionalSettings: true
};

export const defaultContractTermsErrorMessage: Record<string, Record<string, string>> = {
    revenue: {
        revSharePercent: ''
    },
    vendorSharePlayCountCalcStrategy: {
        vendorShareStrategy: '',
        vendorShareStrategyForEarningSubmission: ''
    },
    rateMultipliers: {
        annual: '',
        asdExtendedTrial2m: '',
        asdExtendedTrial3m: '',
        daily: '',
        extendedTrial2m: '',
        extendedTrial3m: '',
        extendedTrial4m: '',
        extendedTrial5m: '',
        extendedTrial6m: '',
        priceDrop3m: '',
        priceDrop4m: '',
        priceDrop6m: '',
        weekly: '',
        winback: ''
    },
    asdRatchets: {
        rollingMonthlyAverage: '',
        tier2Threshold: '',
        tier3Threshold: ''
    },
    thirdPartyDeductionCaps: {
        globalDeductionCap: '',
        appStore: '',
        carrierBilling: '',
        table: ''
    },
    subscriberVolumeDiscount: {
        threshold: '',
        ratesReducedByFraction: ''
    },
    additionalSettings: {
        studentTrueUpThresholdValue: ''
    },
    prsDeductionCap: {
        value: ''
    },
    streamingTaxDeductionCaps: {
        table: ''
    }
};

export const defaultContractTermOptionalSection: string[] = [
    'asdRatchets',
    'subscriberVolumeDiscount',
    'additionalSettings'
];

export const CORE_RATES_COMMON_COLUMNS = [
    RateCardCoreRateGridColumnName.IndMonthlyPSM,
    RateCardCoreRateGridColumnName.HDIndMonthlyPSM,
    RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM,
    RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM,
    RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM,
    RateCardCoreRateGridColumnName.FamMonthlyPSM,
    RateCardCoreRateGridColumnName.HDFamMonthlyPSM,
    RateCardCoreRateGridColumnName.StudentMonthlyPSM,
    RateCardCoreRateGridColumnName.NinetyDayFTCPP,
    RateCardCoreRateGridColumnName.HDNinetyDayFTCPP,
    RateCardCoreRateGridColumnName.BlendedPSMFloor,
    RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM,
    RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM,
    RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP
];

export const ASD_TIER_23_COLUMNS = [
    RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM,
    RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM
];

export const CORE_RATES_CSV_COLUMNS = [
    RateCardCoreRateGridColumnName.Territory,
    RateCardCoreRateGridColumnName.RatecardCurrency,
    ...CORE_RATES_COMMON_COLUMNS
];

export const CORE_RATES_GRID_COLUMNS = [
    RateCardCoreRateGridColumnName.Territory,
    RateCardCoreRateGridColumnName.RatecardCurrency,
    RateCardCoreRateGridColumnName.SettlementCurrency,
    RateCardCoreRateGridColumnName.CAE,
    ...CORE_RATES_COMMON_COLUMNS
];

// plan id => plan display name / core rates grid column name
export const CORE_PLAN_MAP: Map<string, string> = new Map([
    [CorePlan.HAWKFIRE_ALL_DEVICES_MONTHLY, RateCardCoreRateGridColumnName.IndMonthlyPSM],
    [CorePlan.HAWKFIRE_KATANA_IND_MONTHLY, RateCardCoreRateGridColumnName.HDIndMonthlyPSM],
    [CorePlan.HAWKFIRE_HOME_ONLY_MONTHLY, RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM],
    [CorePlan.HAWKFIRE_FAMILY_MONTHLY, RateCardCoreRateGridColumnName.FamMonthlyPSM],
    [CorePlan.HAWKFIRE_KATANA_FAMILY_MONTHLY, RateCardCoreRateGridColumnName.HDFamMonthlyPSM],
    [CorePlan.HAWKFIRE_STUDENT_MONTHLY, RateCardCoreRateGridColumnName.StudentMonthlyPSM],
    [CorePlan.AMU_AB_ALL_DEVICES_MONTHLY, RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM],
    [CorePlan.AMU_AB_FAMILY_MONTHLY, RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM]
]);

export const CPP_CORE_PLAN_MAP: Map<string, string> = new Map([
    [CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL, RateCardCoreRateGridColumnName.NinetyDayFTCPP],
    [CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT, RateCardCoreRateGridColumnName.HDNinetyDayFTCPP],
    [CorePlan.AMU_AB_ALL_DEVICES_90DAY_FREE_TRIAL, RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP]
]);

export const CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP: Map<string, string> = new Map([
    [PaperPlanCorePlan.IndM, RateCardCoreRateGridColumnName.IndMonthlyPSM],
    [PaperPlanCorePlan.HDIndM, RateCardCoreRateGridColumnName.HDIndMonthlyPSM],
    [PaperPlanCorePlan.FamM, RateCardCoreRateGridColumnName.FamMonthlyPSM],
    [PaperPlanCorePlan.HDFamM, RateCardCoreRateGridColumnName.HDFamMonthlyPSM],
    [PaperPlanCorePlan.ASDM, RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM],
    [PaperPlanCorePlan.StudentM, RateCardCoreRateGridColumnName.StudentMonthlyPSM],
    [PaperPlanCorePlan.NinetyDayFT, RateCardCoreRateGridColumnName.NinetyDayFTCPP],
    [PaperPlanCorePlan.HDNinetyDayFT, RateCardCoreRateGridColumnName.HDNinetyDayFTCPP],
    [PaperPlanCorePlan.MontanaIndM, RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM],
    [PaperPlanCorePlan.MontanaFamM, RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM],
    [PaperPlanCorePlan.MontanaNinetyDayFT, RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP]
]);

export const CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP: Map<string, RoyaltyModelsEnum> = new Map([
    [PaperPlanCorePlan.IndM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.HDIndM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.FamM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.HDFamM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.ASDM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.StudentM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.MontanaIndM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.MontanaFamM, RoyaltyModelsEnum.PSM],
    [PaperPlanCorePlan.NinetyDayFT, RoyaltyModelsEnum.CPP],
    [PaperPlanCorePlan.HDNinetyDayFT, RoyaltyModelsEnum.CPP],
    [PaperPlanCorePlan.MontanaNinetyDayFT, RoyaltyModelsEnum.CPP]
]);

export const RATE_CARD_STATUS_TAG_THEMES = {
    [RateCardStatus.Draft]: 'theme',
    [RateCardStatus.Inactive]: 'neutral',
    [RateCardStatus.Active]: 'success',
    [RateCardStatus.Archived]: 'neutral'
};

export const UMG_PERF_DISCOUNT_TYPE_NAMES = [
    UmgPerfDiscountType.SubscriberVolume,
    UmgPerfDiscountType.AllMusic,
    UmgPerfDiscountType.Churn,
    UmgPerfDiscountType.FreeTrialConversion
];

export const MULTIPLIER_NAME_MAP: Map<string, Record<string, string>> = new Map([
    [
        RateMultiplierTypesEnum.ANNUAL,
        { contractTermMultiplierKey: ContractTermMultiplierKey.ANNUAL, displayName: RateMultiplierDisplayName.ANNUAL }
    ],
    [
        RateMultiplierTypesEnum.DAILY,
        { contractTermMultiplierKey: ContractTermMultiplierKey.DAILY, displayName: RateMultiplierDisplayName.DAILY }
    ],
    [
        RateMultiplierTypesEnum.WEEKLY,
        { contractTermMultiplierKey: ContractTermMultiplierKey.WEEKLY, displayName: RateMultiplierDisplayName.WEEKLY }
    ],
    [
        RateMultiplierTypesEnum.PRICE_DROP_3M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.PRICE_DROP_3M,
            displayName: RateMultiplierDisplayName.PRICE_DROP_3M
        }
    ],
    [
        RateMultiplierTypesEnum.PRICE_DROP_4M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.PRICE_DROP_4M,
            displayName: RateMultiplierDisplayName.PRICE_DROP_4M
        }
    ],
    [
        RateMultiplierTypesEnum.PRICE_DROP_6M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.PRICE_DROP_6M,
            displayName: RateMultiplierDisplayName.PRICE_DROP_6M
        }
    ],
    [
        RateMultiplierTypesEnum.WINBACK,
        { contractTermMultiplierKey: ContractTermMultiplierKey.WINBACK, displayName: RateMultiplierDisplayName.WINBACK }
    ],
    [
        RateMultiplierTypesEnum.EXTENDED_TRIAL_2M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.EXTENDED_TRIAL_2M,
            displayName: RateMultiplierDisplayName.EXTENDED_TRIAL_2M
        }
    ],
    [
        RateMultiplierTypesEnum.EXTENDED_TRIAL_3M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.EXTENDED_TRIAL_3M,
            displayName: RateMultiplierDisplayName.EXTENDED_TRIAL_3M
        }
    ],
    [
        RateMultiplierTypesEnum.EXTENDED_TRIAL_4M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.EXTENDED_TRIAL_4M,
            displayName: RateMultiplierDisplayName.EXTENDED_TRIAL_4M
        }
    ],
    [
        RateMultiplierTypesEnum.EXTENDED_TRIAL_5M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.EXTENDED_TRIAL_5M,
            displayName: RateMultiplierDisplayName.EXTENDED_TRIAL_5M
        }
    ],
    [
        RateMultiplierTypesEnum.EXTENDED_TRIAL_6M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.EXTENDED_TRIAL_6M,
            displayName: RateMultiplierDisplayName.EXTENDED_TRIAL_6M
        }
    ],
    [
        RateMultiplierTypesEnum.ASD_EXTENDED_TRIAL_2M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.ASD_EXTENDED_TRIAL_2M,
            displayName: RateMultiplierDisplayName.ASD_EXTENDED_TRIAL_2M
        }
    ],
    [
        RateMultiplierTypesEnum.ASD_EXTENDED_TRIAL_3M,
        {
            contractTermMultiplierKey: ContractTermMultiplierKey.ASD_EXTENDED_TRIAL_3M,
            displayName: RateMultiplierDisplayName.ASD_EXTENDED_TRIAL_3M
        }
    ],
    [
        RateMultiplierTypesEnum.CUSTOM,
        {
            contractTermMultiplierKey: '',
            displayName: RateMultiplierDisplayName.CUSTOM
        }
    ],
    [
        RateMultiplierTypesEnum.NONE,
        {
            contractTermMultiplierKey: '',
            displayName: RateMultiplierDisplayName.NONE
        }
    ]
]);

export const APP_STORE_DEDUCTION_CAP_OPTIONS = ['', 'End user price', 'Wholesale price'];

export const BASE_PLAN_OPTIONS = ['', 'Ind Mthly', 'Fam Mthly', 'ASD Mthly', 'Stdnt Mthly'];

export const BASE_PLAN_OPTION_TO_COLDEF = {
    'Ind Mthly': 'ind_monthly',
    'Fam Mthly': 'fam_monthly',
    'ASD Mthly': 'asd_monthly',
    'Stdnt Mthly': 'stdnt_monthly'
};

export const BASE_PLAN_COLDEF_TO_OPTION = {
    ind_monthly: 'Ind Mthly',
    fam_monthly: 'Fam Mthly',
    asd_monthly: 'ASD Mthly',
    stdnt_monthly: 'Stdnt Mthly'
};

export const ASD_RATCHETS_THRESHOLD_OPERATOR_OPTIONS = ['>=', '>', '<=', '<'];

export const SPATIAL_PLAY_DISCOUNT_THRESHOLD_TYPE_OPTIONS = ['', '3D Plays', '3D Tracks'];

export const SPATIAL_PLAY_DISCOUNT_PLAN_OPTIONS = ['', 'All Plans', 'Royalty Bearing Plans'];

export const TEMPLATE_RATE_CARD_DESELECT_OPTION: IRateCardsRowData[] = [
    {
        contractCreatedBy: 'test_user',
        contractCreationDate: new Date(),
        contractId: -1, // negative integer used as a placeholder
        contractIsDeleted: false,
        contractIsDraft: false,
        contractName: 'Deselect template contract',
        serviceId: -1, // negative integer used as a placeholder
        serviceName: 'test_service',
        entityActiveIntervals: []
    }
];

export interface ICorePlanMetadata {
    paperPlanName: string;
    royaltyModelCode: string;
    royaltyModelTypeCode: string;
    royaltyModelTypeCodes?: string[];
}

type CoreRateGridCol = keyof IRateCardCoreRateGridRowData;

type ICorePlanMapping = {
    [colName: CoreRateGridCol]: ICorePlanMetadata;
};

export const CORE_PLAN_MAPPING: ICorePlanMapping = {
    [RateCardCoreRateGridColumnName.IndMonthlyPSM]: {
        paperPlanName: CorePlan.HAWKFIRE_ALL_DEVICES_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.HDIndMonthlyPSM]: {
        paperPlanName: CorePlan.HAWKFIRE_KATANA_IND_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM]: {
        paperPlanName: CorePlan.HAWKFIRE_HOME_ONLY_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.FamMonthlyPSM]: {
        paperPlanName: CorePlan.HAWKFIRE_FAMILY_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.HDFamMonthlyPSM]: {
        paperPlanName: CorePlan.HAWKFIRE_KATANA_FAMILY_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.StudentMonthlyPSM]: {
        paperPlanName: CorePlan.HAWKFIRE_STUDENT_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.NinetyDayFTCPP]: {
        paperPlanName: CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL,
        royaltyModelCode: RoyaltyModelsEnum.CPP,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.CPP,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.CPP]
    },
    [RateCardCoreRateGridColumnName.HDNinetyDayFTCPP]: {
        paperPlanName: CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT,
        royaltyModelCode: RoyaltyModelsEnum.CPP,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.CPP,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.CPP]
    },
    [RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM]: {
        paperPlanName: CorePlan.AMU_AB_ALL_DEVICES_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    },
    [RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP]: {
        paperPlanName: CorePlan.AMU_AB_ALL_DEVICES_90DAY_FREE_TRIAL,
        royaltyModelCode: RoyaltyModelsEnum.CPP,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.CPP,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.CPP]
    },
    [RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM]: {
        paperPlanName: CorePlan.AMU_AB_FAMILY_MONTHLY,
        royaltyModelCode: RoyaltyModelsEnum.PSM,
        royaltyModelTypeCode: RoyaltyModelTypesEnum.REV_PSM,
        royaltyModelTypeCodes: [RoyaltyModelTypesEnum.REV_PSM, RoyaltyModelTypesEnum.PSM]
    }
};

export const wmgBlendedRateGridColIdSuffix = 'blended';

export const wmgBlendedRateGridColNameSuffix = ' Blended rate';

export const CUSTOMER_TYPE_RATES_MESSAGE = 'Enable different rates per customer type (Paid, Free-Trial, Preview, etc.)';
